import { forwardRef, type ReactNode } from 'react';
import styles from './InlineAlert.module.css';

export type AlertSeverity = 'error' | 'success' | 'info' | 'warning';

export interface InlineAlertProps {
  severity?: AlertSeverity;
  /** When empty/undefined the container stays mounted (for aria-live) but hidden. */
  children?: ReactNode;
  /** Optional support reference (rendered as a small mono "Ref:" line). */
  requestId?: string;
  id?: string;
  className?: string;
}

/**
 * Announces messages to assistive tech. `error` uses `role="alert"` (assertive);
 * all other severities use `role="status"` + `aria-live="polite"`. The container
 * is ALWAYS rendered so screen readers observe content changes in the live region.
 */
export const InlineAlert = forwardRef<HTMLDivElement, InlineAlertProps>(function InlineAlert(
  { severity = 'info', children, requestId, id, className },
  ref,
) {
  const hasContent = children !== undefined && children !== null && children !== false;
  const isError = severity === 'error';

  const classes = [styles.alert, styles[severity], hasContent ? '' : styles.empty, className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      id={id}
      className={classes}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      // Focusable so submit handlers can move focus to the summary.
      tabIndex={-1}
    >
      {hasContent ? (
        <div className={styles.body}>
          <span>{children}</span>
          {requestId ? <span className={styles.ref}>Ref: {requestId}</span> : null}
        </div>
      ) : null}
    </div>
  );
});
